import { useMemo, useRef } from "react";
import { FormProvider, UseFormReturn, FieldErrors } from "react-hook-form";
import {
  AlertCircle,
  ClipboardCheck,
  FileText,
  Loader2,
  Save,
  Send,
  Settings2,
} from "lucide-react";

import { Button, buttonVariants } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UnsavedChangesDialog } from "@/components/dialogs/UnsavedChangesDialog";
import { WizardShell } from "@/components/campos-formulario/form-shell/WizardShell";
import { FormSection } from "@/components/campos-formulario/form-shell/types";
import {
  UseFormWizardReturn,
  useFormWizard,
} from "@/components/campos-formulario/form-shell/useFormWizard";
import { UseFormExitGuardReturn } from "@/hooks/use-form-exit-guard";
import { ComunicadoSistemaFormData } from "@/validations/comunicado-sistema-validation-schema";

import { ConteudoSection } from "./ConteudoSection";
import { ConfiguracaoSection } from "./ConfiguracaoSection";
import { RevisaoSection } from "./RevisaoSection";

export interface ComunicadoFormController {
  methods: UseFormReturn<ComunicadoSistemaFormData>;
  onSubmit: (data: ComunicadoSistemaFormData) => void;
  onError: (errors: FieldErrors<ComunicadoSistemaFormData>) => void;
  handleCancel: () => void;
  exitGuard: UseFormExitGuardReturn;
  isSubmitting: boolean;
  isSubmitDisabled: boolean;
  isEdit?: boolean;
  submitLabel: string;
  /** Só na criação: salva e já publica (o comunicado nasce rascunho). */
  onPublicar?: () => void;
}

/**
 * Corpo do formulário de comunicado em etapas (wizard), espelhando o
 * `AtendimentoCreateForm`. Criar e editar compartilham as mesmas seções; a
 * edição libera todos os passos (`furthestStep` no último).
 */
export function ComunicadoSistemaForm({ controller }: { controller: ComunicadoFormController }) {
  const {
    methods,
    onSubmit,
    onError,
    handleCancel,
    exitGuard,
    isSubmitting,
    isSubmitDisabled,
    isEdit,
    submitLabel,
    onPublicar,
  } = controller;
  const { errors } = methods.formState;
  // `onPublicar` muda de identidade a cada render (handleSubmit); o que os passos
  // precisam saber é só se o botão existe.
  const podePublicar = Boolean(onPublicar);

  const wizardRef = useRef<UseFormWizardReturn | null>(null);

  const steps = useMemo<FormSection<ComunicadoSistemaFormData>[]>(
    () => [
      {
        id: "conteudo",
        title: "Conteúdo",
        description: "Título, resumo e o corpo do comunicado.",
        icon: <FileText className="size-5" />,
        fields: ["titulo", "resumo"],
        content: <ConteudoSection />,
      },
      {
        id: "configuracao",
        title: "Configuração",
        description: "Nível, janela de vigência e público-alvo.",
        icon: <Settings2 className="size-5" />,
        fields: ["nivel", "inicioEm", "fimEm", "cargos", "areas"],
        content: <ConfiguracaoSection />,
      },
      {
        id: "revisao",
        title: "Revisão",
        description: "Confira como vai aparecer antes de salvar.",
        icon: <ClipboardCheck className="size-5" />,
        content: (
          <RevisaoSection
            onEdit={(stepIndex) => wizardRef.current?.goToStep(stepIndex)}
            podePublicar={podePublicar}
          />
        ),
      },
    ],
    [podePublicar],
  );

  const wizard = useFormWizard(steps, methods.trigger, isEdit ? steps.length - 1 : 0);
  wizardRef.current = wizard;

  const errorByStep = steps.map((s) =>
    Boolean(s.fields?.some((f) => (errors as Record<string, unknown>)[f as string])),
  );
  const pendingTitles = steps.filter((_, i) => errorByStep[i]).map((s) => s.title);

  const disabledHint = pendingTitles.length
    ? `Revise para continuar: ${pendingTitles.join(", ")}.`
    : "Preencha os campos obrigatórios.";

  const submit = methods.handleSubmit(onSubmit, onError);

  const submitSlot = (
    <TooltipProvider>
      {onPublicar && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={isSubmitDisabled}
          onClick={onPublicar}
          className="mr-2"
        >
          <Send className="mr-1 h-4 w-4" /> Salvar e publicar
        </Button>
      )}
      <Tooltip>
        <TooltipTrigger
          type="submit"
          className={buttonVariants({
            size: "sm",
            variant: isSubmitDisabled && !isSubmitting ? "destructive" : "default",
            className: "min-w-[140px]",
          })}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Salvando...
            </>
          ) : isSubmitDisabled ? (
            <>
              <AlertCircle className="mr-1 h-4 w-4" /> {submitLabel}
            </>
          ) : (
            <>
              <Save className="mr-1 h-4 w-4" /> {submitLabel}
            </>
          )}
        </TooltipTrigger>
        {isSubmitDisabled && !isSubmitting && (
          <TooltipContent className="max-w-[240px]">{disabledHint}</TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <FormProvider {...methods}>
      <Form {...methods}>
        <WizardShell
          steps={steps}
          wizard={wizard}
          onSubmit={submit}
          onCancel={handleCancel}
          submitSlot={submitSlot}
          isSubmitting={isSubmitting}
          errorSteps={errorByStep}
        />
      </Form>

      <UnsavedChangesDialog
        open={exitGuard.isConfirmOpen}
        onOpenChange={exitGuard.setIsConfirmOpen}
        onConfirm={exitGuard.confirmExit}
        title="Descartar preenchimento?"
        description="O que você preencheu até aqui será perdido."
        confirmLabel="Descartar"
      />
    </FormProvider>
  );
}
